import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Settings, Users, Mail, Smartphone, BarChart3, 
  Plus, Edit, Trash2, Eye, Send, Filter, Search,
  Heart, UserPlus, ShoppingCart, Calendar, Shield,
  Megaphone, CheckCircle, AlertTriangle, Clock,
  Folder, QrCode, MessageCircle, Star, Save, X,
  RefreshCw, Activity, TrendingUp, Award
} from 'lucide-react';
import { 
  getNotificationCategories,
  getNotificationSections, 
  getNotificationStats,
  sendTestNotification,
  deactivateNotificationCategory,
  sendSystemNotification,
  sendNotificationToUsers,
  getUsersForNotificationTargeting
} from '../utils/adminApi';
import '../styles/NotificationManagementAdmin.css';
import OverviewTab from './notifications/OverviewTab';
import ActivityFeedTab from './notifications/ActivityFeedTab';
import AnalyticsTab from './notifications/AnalyticsTab';
import WorkflowsTab from './notifications/WorkflowsTab';

const NotificationManagementAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');

  // New state for sending notifications
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    category_key: '',
    target_type: 'all_users', // 'all_users', 'user_types', 'specific_users'
    user_types: [],
    specific_users: [],
    priority: 1,
    include_email: false,
    test_mode: false
  });
  const [targetUsers, setTargetUsers] = useState([]);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [selectedTestUser, setSelectedTestUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  // Load notification data using the new API functions
  useEffect(() => {
    loadNotificationData();
  }, []);

  const loadNotificationData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the new API functions from adminApi.js
      const [
        sectionsData,
        categoriesData,
        statsData
      ] = await Promise.all([
        getNotificationSections(),
        getNotificationCategories(),
        getNotificationStats()
      ]);

      setSections(sectionsData);
      setCategories(categoriesData);
      setAnalytics(statsData);

    } catch (err) {
      console.error('Error loading notification data:', err);
      setError(err.message || 'Failed to load notification data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      'heart': Heart,
      'user-plus': UserPlus,
      'shopping-cart': ShoppingCart,
      'calendar': Calendar,
      'shield': Shield,
      'megaphone': Megaphone,
      'clock': Clock,
      'folder': Folder,
      'qr-code': QrCode,
      'message-circle': MessageCircle,
      'star': Star,
      'bell': Bell,
      'mail': Mail,
      'smartphone': Smartphone,
      'award': Award,
      'activity': Activity
    };
    
    const IconComponent = iconMap[iconName] || Bell;
    return <IconComponent className="notification-category-icon" />;
  };

  // Filter categories based on search and section
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.category_key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSection = sectionFilter === 'all' || category.section === sectionFilter;
    return matchesSearch && matchesSection;
  });

  // Handle category operations using the new API functions
  const handleSendTestNotification = async (categoryKey) => {
    try {
      const result = await sendTestNotification(categoryKey, 'admin-test-user'); // You can pass actual admin user ID
      setNotification({ 
        type: result.success ? 'success' : 'error', 
        message: result.message 
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to send test notification' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to deactivate this category?')) {
      try {
        const result = await deactivateNotificationCategory(categoryId, 'admin-test-user');
        setNotification({ 
          type: result.success ? 'success' : 'error', 
          message: result.message 
        });
        if (result.success) {
          loadNotificationData(); // Reload data
        }
        setTimeout(() => setNotification(null), 3000);
      } catch (error) {
        setNotification({ type: 'error', message: 'Failed to deactivate category' });
        setTimeout(() => setNotification(null), 3000);
      }
    }
  };

  // New functions for sending notifications
  const handleSendNotification = async () => {
    if (!sendForm.title || !sendForm.message || !sendForm.category_key) {
      setNotification({ type: 'error', message: 'Please fill in all required fields' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    setSending(true);
    try {
      let result;
      let targetUserIds = [];
      
      if (sendForm.target_type === 'specific_users' && sendForm.specific_users.length > 0) {
        // Use specific users provided
        targetUserIds = sendForm.specific_users;
      } else {
        // Fetch users based on target type
        const userTypes = sendForm.target_type === 'user_types' && sendForm.user_types.length > 0
          ? sendForm.user_types 
          : ['artist', 'collector', 'gallery'];
        
        console.log('Fetching users for types:', userTypes);
        
        const targetUsers = await getUsersForNotificationTargeting({
          user_types: userTypes,
          has_app_only: true, // Only send to mobile app users
          limit: 1000
        });
        
        targetUserIds = targetUsers.map(user => user.uuid);
        console.log(`Found ${targetUserIds.length} target users`);
        
        if (targetUserIds.length === 0) {
          setNotification({ 
            type: 'error', 
            message: 'No mobile users found matching the selected criteria' 
          });
          setSending(false);
          setTimeout(() => setNotification(null), 5000);
          return;
        }
      }
      
      // If test mode, only send to selected test user
      const finalTargetIds = sendForm.test_mode && selectedTestUser 
        ? [selectedTestUser] 
        : targetUserIds;
      
      console.log(`Sending to ${finalTargetIds.length} user(s)${sendForm.test_mode ? ' (TEST MODE)' : ''}`);
      
      // Send to all target users (or just test user)
      result = await sendSystemNotification(
        finalTargetIds,
        sendForm.title,
        sendForm.message,
        sendForm.category_key,
        sendForm.priority,
        null // TODO: Pass actual admin user object
      );

      const testModeMsg = sendForm.test_mode ? '🧪 TEST: ' : '';
      setNotification({ 
        type: result.success ? 'success' : 'error', 
        message: result.success 
          ? `${testModeMsg}Notification sent to ${result.successful} of ${result.total_sent} user${result.total_sent !== 1 ? 's' : ''}! ${result.failed > 0 ? `(${result.failed} failed)` : ''}${sendForm.test_mode ? ' Check OneSignal.' : ''}` 
          : result.error || 'Failed to send notification'
      });
      
      setShowSendModal(false);
      setSendForm({
        title: '',
        message: '',
        category_key: '',
        target_type: 'all_users',
        user_types: [],
        specific_users: [],
        priority: 1,
        include_email: false,
        test_mode: true
      });
      
      // Reload dashboard data
      loadNotificationData();
      
    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: `Failed to send notification: ${error.message}` 
      });
    } finally {
      setSending(false);
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const loadTargetUsers = async () => {
    if (sendForm.target_type !== 'specific_users') return;
    
    setLoadingUsers(true);
    try {
      const users = await getUsersForNotificationTargeting({
        user_types: sendForm.user_types,
        limit: 50
      });
      setTargetUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load preview of users who will receive notification
  const loadPreviewUsers = async () => {
    setLoadingPreview(true);
    try {
      const userTypes = sendForm.target_type === 'user_types' && sendForm.user_types.length > 0
        ? sendForm.user_types 
        : ['artist', 'collector', 'gallery'];
      
      console.log('Loading preview for user types:', userTypes);
      
      const users = await getUsersForNotificationTargeting({
        user_types: userTypes,
        has_app_only: true,
        limit: 1000 // Increase limit to get all users
      });
      
      console.log(`Preview loaded: ${users.length} users`);
      
      setPreviewUsers(users);
      if (users.length > 0 && !selectedTestUser) {
        setSelectedTestUser(users[0].uuid); // Auto-select first user
      }
    } catch (error) {
      console.error('Error loading preview users:', error);
      setPreviewUsers([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Load preview when modal opens or filters change
  React.useEffect(() => {
    if (showSendModal) {
      loadPreviewUsers();
    }
  }, [showSendModal, sendForm.target_type, JSON.stringify(sendForm.user_types)]);

  if (loading) {
    return (
      <div className="notification-management-admin">
        <div className="notification-admin-container">
          <div className="notification-loading">
            <div className="notification-loading-spinner"></div>
            <p className="notification-loading-text">Loading notification data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-management-admin">
        <div className="notification-admin-container">
          <div className="notification-loading">
            <AlertTriangle className="notification-empty-icon" color="#dc3545" />
            <h3 className="notification-empty-title">Error Loading Data</h3>
            <p className="notification-empty-description">{error}</p>
            <button
              onClick={loadNotificationData}
              className="notification-btn notification-btn-primary"
            >
              <RefreshCw className="notification-btn-icon" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-management-admin">
      <div className="notification-admin-container">
        {/* Header */}
        <div className="notification-admin-header">
          <div className="notification-header-content">
            <h1 className="notification-admin-title">
              <Bell className="notification-tab-icon" color="#007bff" />
              Notification Management
            </h1>
            <p className="notification-admin-subtitle">
              Manage notification categories, preferences, and delivery across the platform
            </p>
          </div>
          <div className="notification-header-actions">
            <button
              onClick={() => navigate('/admin/notifications/send')}
              className="notification-btn notification-btn-primary"
            >
              <Send className="notification-btn-icon" color="#ffffff" />
              Send Notification
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="notification-btn notification-btn-secondary"
            >
              <Plus className="notification-btn-icon" color="#6c757d" />
              New Category
            </button>
            <button
              onClick={loadNotificationData}
              className="notification-btn notification-btn-secondary"
            >
              <RefreshCw className="notification-btn-icon" color="#6c757d" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="notification-tab-navigation">
          <ul className="notification-tab-list">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'activity', label: 'Activity Feed', icon: Activity },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
              { id: 'workflows', label: 'Workflows', icon: Bell },
              { id: 'categories', label: 'Categories', icon: Settings, badge: categories.length },
            ].map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`notification-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <tab.icon className="notification-tab-icon" color={tab.id === 'overview' ? '#007bff' : tab.id === 'categories' ? '#28a745' : '#fd7e14'} />
                  {tab.label}
                  {tab.badge && (
                    <span className="notification-tab-badge">{tab.badge}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Tab Content */}
        <div className="notification-tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab onTabChange={setActiveTab} />
          )}

          {/* Activity Feed Tab */}
          {activeTab === 'activity' && (
            <ActivityFeedTab />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <AnalyticsTab />
          )}

          {/* Workflows Tab */}
          {activeTab === 'workflows' && (
            <WorkflowsTab />
          )}

          {/* Categories Tab - keep existing content */}
          {activeTab === 'categories' && (
            <div>
              {/* Controls */}
              <div className="notification-controls">
                <div className="notification-controls-left">
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: '#6b7280' }} />
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="notification-search-input"
                      style={{ paddingLeft: '2.5rem' }}
                    />
                  </div>
                  <select
                    value={sectionFilter}
                    onChange={(e) => setSectionFilter(e.target.value)}
                    className="notification-filter-select"
                  >
                    <option value="all">All Sections</option>
                    {sections.map((section) => (
                      <option key={section.section_key} value={section.section_key}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="notification-controls-right">
                  <button
                    onClick={() => setShowSendModal(true)}
                    className="notification-btn notification-btn-primary"
                  >
                    <Send className="notification-btn-icon" />
                    Send Notification
                  </button>
                  <button className="notification-btn notification-btn-secondary">
                    <Filter className="notification-btn-icon" color="#6c757d" />
                    Filter
                  </button>
                </div>
              </div>

              {/* Categories Table */}
              {filteredCategories.length > 0 ? (
                <div className="notification-table-container">
                  <table className="notification-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Section</th>
                        <th>User Types</th>
                        <th>Settings</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((category) => (
                        <tr key={category.id}>
                          <td>
                            <div className="notification-category-cell">
                              {getIconComponent(category.icon)}
                              <div className="notification-category-info">
                                <div className="notification-category-title">{category.title}</div>
                                <div className="notification-category-key">{category.category_key}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="notification-section-badge">
                              {sections.find(s => s.section_key === category.section)?.title}
                            </span>
                          </td>
                          <td>
                            <div className="notification-user-types">
                              {(Array.isArray(category.user_types) ? category.user_types :
                                JSON.parse(category.user_types || '[]')).map(type => (
                                <span key={type} className="notification-user-type-tag">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="notification-settings-list">
                              <div className="notification-setting-item">
                                <span className={`notification-setting-indicator ${category.default_push ? 'enabled' : 'disabled'}`}></span>
                                <span className="notification-setting-label">Push</span>
                              </div>
                              <div className="notification-setting-item">
                                <span className={`notification-setting-indicator ${category.default_in_app ? 'enabled' : 'disabled'}`}></span>
                                <span className="notification-setting-label">In-App</span>
                              </div>
                              <div className="notification-setting-item">
                                <span className={`notification-setting-indicator ${category.default_enabled ? 'enabled' : 'disabled'}`}></span>
                                <span className="notification-setting-label">Email</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`notification-status-badge ${category.is_active ? 'active' : 'inactive'}`}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="notification-actions">
                              <button
                                onClick={() => setEditingCategory(category)}
                                className="notification-action-button edit"
                                title="Edit category"
                              >
                                <Edit style={{ width: '1rem', height: '1rem' }} />
                              </button>
                              <button
                                onClick={() => handleSendTestNotification(category.category_key)}
                                className="notification-action-button send"
                                title="Send test notification"
                              >
                                <Send style={{ width: '1rem', height: '1rem' }} />
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="notification-action-button delete"
                                title="Delete category"
                              >
                                <Trash2 style={{ width: '1rem', height: '1rem' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="notification-empty-state">
                  <Bell className="notification-empty-icon" />
                  <h3 className="notification-empty-title">No Categories Found</h3>
                  <p className="notification-empty-description">
                    {searchTerm || sectionFilter !== 'all'
                      ? 'No categories match your current filters.'
                      : 'Get started by creating your first notification category.'
                    }
                  </p>
                  {!searchTerm && sectionFilter === 'all' && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="notification-btn notification-btn-primary"
                    >
                      <Plus className="notification-btn-icon" />
                      Create Category
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Send Notification Modal */}
        {showSendModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>Send Notification</h2>
                <button 
                  onClick={() => setShowSendModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                >
                  <X style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Title Input */}
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={sendForm.title}
                    onChange={(e) => setSendForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Message Input */}
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Message *
                  </label>
                  <textarea
                    value={sendForm.message}
                    onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Notification message"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Category *
                  </label>
                  <select
                    value={sendForm.category_key}
                    onChange={(e) => setSendForm(prev => ({ ...prev, category_key: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.category_key} value={category.category_key}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Type */}
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Send To
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        checked={sendForm.target_type === 'all_users'}
                        onChange={() => setSendForm(prev => ({ ...prev, target_type: 'all_users' }))}
                      />
                      All Users
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        checked={sendForm.target_type === 'user_types'}
                        onChange={() => setSendForm(prev => ({ ...prev, target_type: 'user_types' }))}
                      />
                      Specific User Types
                    </label>
                  </div>

                  {/* User Types Selection */}
                  {sendForm.target_type === 'user_types' && (
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                      {['artist', 'collector', 'gallery'].map(type => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={sendForm.user_types.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSendForm(prev => ({ ...prev, user_types: [...prev.user_types, type] }));
                              } else {
                                setSendForm(prev => ({ ...prev, user_types: prev.user_types.filter(t => t !== type) }));
                              }
                            }}
                          />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Priority */}
                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Priority
                  </label>
                  <select
                    value={sendForm.priority}
                    onChange={(e) => setSendForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value={1}>Normal</option>
                    <option value={2}>High</option>
                    <option value={3}>Urgent</option>
                  </select>
                </div>

                {/* Preview Recipients */}
                <div style={{ 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '0.5rem', 
                  padding: '1rem',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      Recipients Preview
                    </h4>
                    {loadingPreview ? (
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Loading...</span>
                    ) : (
                      <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6f42c1' }}>
                        {previewUsers.length} mobile user{previewUsers.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  {previewUsers.length > 0 ? (
                    <div style={{ 
                      maxHeight: '150px', 
                      overflowY: 'auto', 
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      {previewUsers.slice(0, 10).map((user, idx) => (
                        <div key={user.uuid} style={{ padding: '0.25rem 0' }}>
                          {idx + 1}. {user.nickname || user.first_name || user.email} 
                          <span style={{ color: '#9ca3af' }}> ({user.preferences?.home || 'unknown'})</span>
                        </div>
                      ))}
                      {previewUsers.length > 10 && (
                        <div style={{ padding: '0.25rem 0', fontStyle: 'italic' }}>
                          ...and {previewUsers.length - 10} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
                      No mobile users found for selected criteria
                    </p>
                  )}
                </div>

                {/* Test Mode */}
                <div style={{ 
                  backgroundColor: '#fef3c7', 
                  border: '1px solid #fbbf24', 
                  borderRadius: '0.5rem', 
                  padding: '1rem'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={sendForm.test_mode}
                      onChange={(e) => setSendForm(prev => ({ ...prev, test_mode: e.target.checked }))}
                    />
                    <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      🧪 Test Mode - Send to one user only
                    </span>
                  </label>
                  
                  {sendForm.test_mode && previewUsers.length > 0 && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.5rem', color: '#92400e' }}>
                        Select test recipient:
                      </label>
                      <select
                        value={selectedTestUser || ''}
                        onChange={(e) => setSelectedTestUser(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #fbbf24',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          backgroundColor: 'white',
                          boxSizing: 'border-box'
                        }}
                      >
                        {previewUsers.map((user) => (
                          <option key={user.uuid} value={user.uuid}>
                            {user.nickname || user.first_name || user.email} - {user.email}
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.675rem', color: '#92400e', marginTop: '0.5rem', marginBottom: 0 }}>
                        💡 Check OneSignal to see this notification in the Sent Messages
                      </p>
                    </div>
                  )}
                </div>

                {/* Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={sendForm.include_email}
                      onChange={(e) => setSendForm(prev => ({ ...prev, include_email: e.target.checked }))}
                    />
                    Also send email
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '2rem' }}>
                <button
                  onClick={() => setShowSendModal(false)}
                  disabled={sending}
                  className="notification-btn notification-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={sending || !sendForm.title || !sendForm.message || !sendForm.category_key}
                  className="notification-btn notification-btn-primary"
                  style={{ opacity: sending ? 0.6 : 1 }}
                >
                  {sending ? (
                    <>
                      <div className="notification-loading-spinner" style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }}></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="notification-btn-icon" />
                      {sendForm.test_mode 
                        ? `🧪 Send Test to 1 User` 
                        : `Send to ${previewUsers.length} User${previewUsers.length !== 1 ? 's' : ''}`
                      }
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Toast */}
        {notification && (
          <div className="notification-toast">
            <div className={`notification-toast-content ${notification.type}`}>
              {notification.type === 'success' ? (
                <CheckCircle className="notification-toast-icon" />
              ) : (
                <AlertTriangle className="notification-toast-icon" />
              )}
              <span className="notification-toast-message">{notification.message}</span>
              <button
                onClick={() => setNotification(null)}
                className="notification-toast-close"
              >
                <X style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagementAdmin;